// Language preference. Persists users.locale, which drives BOTH the app
// chrome and the language every transactional email is rendered in — so this
// is a real setting, not a duplicate of the header's LangSwitcher (which only
// changes the URL for the current visit).
//
// On success we navigate to the same route under the new locale, so the
// I18nProvider re-mounts and the change is visible immediately rather than on
// the next cold load.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Button, Segmented } from '@fajrarabi/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { AccountSection } from './AccountSection';

import { changeLocale, type Locale } from '~/lib/account';
import { useAuth } from '~/lib/auth';
import { pathInLocale } from '~/lib/locale';

export function LanguagePreferenceCard({ locale }: { locale: Locale }) {
  const t = useT();
  const { user, refresh } = useAuth();
  const [selected, setSelected] = useState<Locale>(user?.locale ?? locale);

  const mutation = useMutation({
    mutationFn: (next: Locale) => changeLocale(next),
    onSuccess: async (_data, next) => {
      await refresh();
      if (next !== locale && typeof window !== 'undefined') {
        // Full navigation, matching LangSwitcher: re-mounts the locale layout
        // so <html lang/dir> and the bundle both switch cleanly.
        window.location.href = pathInLocale(window.location.pathname, next);
      }
    },
  });

  return (
    <AccountSection
      id="account-language"
      title={t('app.account.language_title')}
      description={t('app.account.language_description')}
    >
      <Segmented<Locale>
        ariaLabel={t('app.account.language_title')}
        value={selected}
        onChange={setSelected}
        options={[
          { value: 'ar', label: 'العربية' },
          { value: 'en', label: 'English' },
        ]}
      />

      {mutation.error && <Alert tone="danger">{t('app.common.error_generic')}</Alert>}
      {mutation.isSuccess && selected === locale && (
        <Alert tone="success">{t('app.account.saved')}</Alert>
      )}

      <div className="flex justify-end">
        <Button
          onClick={() => mutation.mutate(selected)}
          disabled={mutation.isPending || selected === user?.locale}
        >
          {t('app.common.save')}
        </Button>
      </div>
    </AccountSection>
  );
}
